import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { useAuth } from '../context/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { 
  Users, ChevronRight, Clock, Check, X, Loader2, 
  Wallet, Eye, Edit, PenLine, Bell, RefreshCw
} from 'lucide-react'
import axios from 'axios'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

export default function SharedWithMePage() {
  const { t } = useLanguage()
  const { token } = useAuth()
  const navigate = useNavigate()
  
  const [sharedSpaces, setSharedSpaces] = useState([])
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedInvitation, setSelectedInvitation] = useState(null)
  const [processingInvite, setProcessingInvite] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      
      // Get shared spaces
      const spacesRes = await axios.get(`${API}/api/collaborations/shared-with-me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSharedSpaces(spacesRes.data.shared_spaces || [])
      
      // Get pending invitations
      const invitesRes = await axios.get(`${API}/api/collaborations/invitations/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setPendingInvitations(invitesRes.data || [])
      
    } catch (error) {
      console.error('Error fetching shared spaces:', error)
      toast.error(t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [token])

  const handleAcceptInvitation = async (inviteId) => {
    try {
      setProcessingInvite(true)
      await axios.post(
        `${API}/api/collaborations/invitations/${inviteId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success(t('collaboration.invitationAccepted'))
      setSelectedInvitation(null)
      fetchData()
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'))
    } finally {
      setProcessingInvite(false)
    }
  }

  const handleRejectInvitation = async (inviteId) => {
    try {
      setProcessingInvite(true)
      await axios.post(
        `${API}/api/collaborations/invitations/${inviteId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success(t('collaboration.invitationRejected'))
      setSelectedInvitation(null)
      fetchData()
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'))
    } finally {
      setProcessingInvite(false)
    }
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'viewer': return <Eye className="w-4 h-4" />
      case 'capturista': return <PenLine className="w-4 h-4" />
      case 'editor': return <Edit className="w-4 h-4" />
      default: return <Eye className="w-4 h-4" />
    }
  }

  const getRoleBadge = (role) => {
    const roleColors = {
      viewer: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      capturista: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      editor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
    }
    const roleLabels = {
      viewer: t('collaboration.roles.viewer'),
      capturista: t('collaboration.roles.capturista'),
      editor: t('collaboration.roles.editor')
    }
    
    return (
      <Badge className={`${roleColors[role]} flex items-center gap-1`}>
        {getRoleIcon(role)}
        {roleLabels[role]}
      </Badge>
    )
  }

  const getModuleIcon = (module) => {
    switch (module) {
      case 'finance': return <Wallet className="w-5 h-5 text-emerald-500" />
      default: return <Users className="w-5 h-5" />
    }
  }

  const handleEnterSpace = (space) => {
    // Navigate to the shared finance module
    if (space.module === 'finance') {
      navigate(`/finance/shared/${space.owner_user_id}`)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="container max-w-2xl mx-auto px-4 py-6 pb-24 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            {t('collaboration.sharedWithMe')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('collaboration.sharedWithMeDesc')}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={fetchData} data-testid="refresh-shared">
          <RefreshCw className="w-5 h-5" />
        </Button>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-500" />
              {t('collaboration.pendingInvitations')} ({pendingInvitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingInvitations.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => setSelectedInvitation(invite)}
                data-testid={`invitation-${invite.id}`}
              >
                <div className="flex items-center gap-3">
                  {getModuleIcon(invite.module)}
                  <div>
                    <p className="font-medium text-sm">
                      {invite.module === 'finance' ? t('collaboration.financeOf', { name: invite.owner_name }) : invite.module}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('collaboration.invitedBy', { name: invite.owner_name })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getRoleBadge(invite.role)}
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Shared Spaces */}
      {sharedSpaces.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">{t('collaboration.activeSpaces')}</h2>
          {sharedSpaces.map((space) => (
            <Card
              key={space.id}
              className="cursor-pointer hover:shadow-md transition-all"
              onClick={() => handleEnterSpace(space)}
              data-testid={`shared-space-${space.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getModuleIcon(space.module)}
                    <div>
                      <h3 className="font-semibold">{space.module_display_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('collaboration.sharedBy', { name: space.owner_name })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getRoleBadge(space.role)}
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
                {space.last_activity_at && (
                  <div className="mt-3 pt-3 border-t flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {t('collaboration.lastActivity')}: {new Date(space.last_activity_at).toLocaleDateString()}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : pendingInvitations.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold mb-2">{t('collaboration.noSharedSpaces')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('collaboration.noSharedSpacesDesc')}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Invitation Detail Modal */}
      <Dialog open={!!selectedInvitation} onOpenChange={() => setSelectedInvitation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedInvitation && getModuleIcon(selectedInvitation.module)}
              {t('collaboration.invitationDetails')}
            </DialogTitle>
            <DialogDescription>
              {selectedInvitation && t('collaboration.invitationFrom', { name: selectedInvitation.owner_name })}
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvitation && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{t('collaboration.module')}</span>
                  <span className="font-medium">
                    {selectedInvitation.module === 'finance' ? t('finance.title') : selectedInvitation.module}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{t('collaboration.role')}</span>
                  {getRoleBadge(selectedInvitation.role)}
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{t('collaboration.owner')}</span>
                  <span className="font-medium">{selectedInvitation.owner_name}</span>
                </div>
              </div>
              
              {selectedInvitation.message && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm italic">"{selectedInvitation.message}"</p>
                </div>
              )}
              
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {t('collaboration.expiresAt')}: {new Date(selectedInvitation.expires_at).toLocaleDateString()}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => handleRejectInvitation(selectedInvitation?.id)}
              disabled={processingInvite}
              className="flex-1"
              data-testid="reject-invitation-btn"
            >
              {processingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
              {t('collaboration.reject')}
            </Button>
            <Button
              onClick={() => handleAcceptInvitation(selectedInvitation?.id)}
              disabled={processingInvite}
              className="flex-1"
              data-testid="accept-invitation-btn"
            >
              {processingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {t('collaboration.accept')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
